import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import { FormEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormItem } from '../../../common/hooks/use-form-item';
import { InputType } from '../../../common/hooks/use-form-item/types';
import { and } from '../../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../../common/hooks/use-form-item/validators/operational';
import { Venafi } from '../../../integrations/venafi/models/Venafi';
import { CheckVenafiIntegrationArgs, UpdateVenafiIntegrationArgs } from '../../../integrations/venafi/services/VenafiRepository';
import { DangerButton, PrimaryButton } from '../../../modal-flows/components/Buttons';
import { ACCORDION_STYLES } from '../../../common/components/IntegrationAccordion';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { url, urlWithoutTrailingSlash } from '../../../common/hooks/use-form-item/validators/type';

type Props = {
  venafi: Venafi | null;
  onTestClick: (args: CheckVenafiIntegrationArgs) => void;
  onConnectClick: (args: UpdateVenafiIntegrationArgs) => void;
  onUnlinkClick: () => void;
  canUpdate: boolean;
};

export const VenafiAccordionForm = (props: Props) => {
  const { venafi, onConnectClick, onTestClick, canUpdate, onUnlinkClick } = props;
  const { t } = useTranslation();
  const isVenafiConfigured = Boolean(venafi);
  const [visible, setVisible] = useState<boolean>(false);

  const [instanceUrl, instanceUrlComponent, isInstanceUrlValid] = useFormItem({
    type: InputType.TYPE_TEXT,
    value: venafi?.getUrl() ?? '',
    validator: and(required(), url(), urlWithoutTrailingSlash(), maxLength(1000)),
    inputOptions: {
      autoFocus: true,
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    label: t('integrationList.venafi.instanceUrlLabel'),
  });
  const [accessToken, accessTokenComponent, isAccessTokenValid] = useFormItem({
    type: visible ? InputType.TYPE_TEXT : InputType.TYPE_PASSWORD,
    value: venafi?.getAccessToken() ?? '',
    inputOptions: {
      // Prevent browsers from trying to prefill the user's password in this field:
      // `one-time-code` seems to be the least unrelated value for a token, see
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
      autoComplete: 'one-time-code',
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    validator: and(required(), maxLength(1000)),
    label: t('integrationList.venafi.accessTokenLabel'),
  });
  const [certificate, certificateComponent] = useFormItem({
    type: InputType.TYPE_TEXTAREA,
    inputOptions: {
      disabled: !canUpdate,
      help: t('integrationList.optional'),
      multiline: true,
    },
    value: venafi?.getTrustedCaCertificate() ?? '',
    validator: maxLength(10_000),
    label: t('integrationList.venafi.certificateLabel'),
  });

  const isFormValid = isAccessTokenValid && isInstanceUrlValid;
  const fieldsModified = instanceUrl !== venafi?.getUrl() || accessToken !== venafi?.getAccessToken() || certificate !== venafi?.getTrustedCaCertificate();
  const canSubmit = isFormValid && fieldsModified;

  const handleConnectButtonClick: FormEventHandler = (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    onConnectClick({
      accessToken,
      url: instanceUrl,
      trustedCaCertificate: certificate,
    });
  };

  const handleTestClick = () => {
    if (!isFormValid) return;

    onTestClick({
      url: instanceUrl,
      accessToken,
      trustedCaCertificate: certificate,
    });
  };

  const toggleVisibility = () => {
    setVisible((prevValue) => !prevValue);
  };

  return (
    <Box sx={{ p: 3, backgroundImage: ACCORDION_STYLES.backgroundImage }}>
      <Stack spacing={1.5} component="form" onSubmit={handleConnectButtonClick}>
        {instanceUrlComponent}
        <Stack direction="row" spacing={1} alignItems="end">
          {accessTokenComponent}
          <IconButton
            data-role="access-token-toggle"
            size="small"
            title={t(visible ? 'toggleVisibility.visible' : 'toggleVisibility.hidden', { field: t('integrationList.venafi.accessTokenLabel') })}
            disabled={!accessToken}
            onClick={toggleVisibility}
          >
            {visible ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Stack>
        {certificateComponent}
        <Divider />
        <Stack direction="row" gap={2}>
          {canUpdate ? (
            <>
              {isVenafiConfigured && <DangerButton onClick={onUnlinkClick}>{t('integrationList.venafi.unlinkButton')}</DangerButton>}
              <PrimaryButton disabled={!isFormValid} onClick={handleTestClick} sx={{ ml: 'auto' }}>
                {t('integrationList.venafi.testButton')}
              </PrimaryButton>
              <PrimaryButton disabled={!canSubmit} type="submit">
                {t('integrationList.venafi.connectButton')}
              </PrimaryButton>
            </>
          ) : (
            <Typography>{t('integrationList.noPermission')}</Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
